/**
 * ============================================================
 * Wingman RC5 — Indoor Airport Intelligence
 * AirportDiscoveryContracts.ts
 *
 * Provider-neutral airport discovery type contracts.
 * Category enums, POI schemas, search request/response types.
 *
 * IAM-M16B-R1.4B — Airport Discovery Category + Search Data
 * ============================================================
 */

// ── Category Taxonomy ────────────────────────────────────────

export enum AirportCategory {
  GATE = 'gate',
  SECURITY = 'security',
  CHECK_IN = 'check_in',
  CUSTOMS = 'customs',
  IMMIGRATION = 'immigration',
  LOUNGE = 'lounge',
  RESTAURANT = 'restaurant',
  RETAIL = 'retail',
  RESTROOM = 'restroom',
  BAGGAGE_CLAIM = 'baggage_claim',
  GROUND_TRANSPORT = 'ground_transport',
  INFORMATION = 'information',
  CONNECTOR = 'connector',
  ELEVATOR = 'elevator',
  ESCALATOR = 'escalator',
  STAIRS = 'stairs',
  CORRIDOR = 'corridor',
  UNKNOWN = 'unknown',
}

// ── POI Schema ───────────────────────────────────────────────

export interface AirportPOI {
  /** Stable unique identifier (provider-agnostic) */
  readonly id: string;
  /** Human-readable display name */
  readonly name: string;
  /** Normalized category */
  readonly category: AirportCategory;
  /** Floor/level number (e.g., 3 = Gates, 4 = Check-in) */
  readonly level: number;
  /** [longitude, latitude] */
  readonly coordinates: readonly [number, number];
  /** Terminal identifier (e.g., "T4") */
  readonly terminal: string;
  /** Optional sub-area (e.g., "Concourse B") */
  readonly concourse?: string;
  /** Raw source-layer identifier from Mapbox Indoor v3 */
  readonly sourceLayer?: string;
  /** Additional metadata from the data provider */
  readonly metadata?: Record<string, unknown>;
}

// ── Search Contract ──────────────────────────────────────────

export interface AirportSearchRequest {
  /** Free-text query (e.g., "Gate B20", "Starbucks") */
  readonly query?: string;
  /** Filter by category */
  readonly category?: AirportCategory;
  /** Filter by level */
  readonly level?: number;
  /** Filter by terminal */
  readonly terminal?: string;
  /** Maximum results to return (default: 20) */
  readonly limit?: number;
}

export interface AirportSearchResult {
  /** The matched POI */
  readonly poi: AirportPOI;
  /** Relevance score 0.0–1.0 */
  readonly score: number;
  /** Match explanation (e.g., "exact name match") */
  readonly matchReason: string;
}

export interface AirportSearchResponse {
  readonly results: readonly AirportSearchResult[];
  readonly totalMatched: number;
  readonly query: AirportSearchRequest;
  readonly timestamp: number;
}

// ── Discovery Service Interface ──────────────────────────────

export interface IAirportDiscoveryService {
  /** Index a batch of POIs for searching */
  index(pois: readonly AirportPOI[]): void;
  /** Search indexed POIs */
  search(request: AirportSearchRequest): AirportSearchResponse;
  /** Get a POI by its stable ID */
  getById(id: string): AirportPOI | undefined;
  /** Get all POIs in a category */
  getByCategory(category: AirportCategory): readonly AirportPOI[];
  /** Total indexed POI count */
  readonly count: number;
}

// ── Normalization Contract ───────────────────────────────────

export interface INormalizationAdapter {
  /** Normalize a raw feature from any provider into an AirportPOI */
  normalize(rawFeature: Record<string, unknown>): AirportPOI | null;
  /** Deduplicate a list of POIs */
  deduplicate(pois: readonly AirportPOI[]): readonly AirportPOI[];
}
